// src/lib/clinical-display-name.ts
// THE one clinical display-name formatter (PROD-1656 Phase A FE, BRD-1 §5.3.2).
// Every surface that renders a clinical term name routes its NAME SOURCE through this
// function — replacing the ad-hoc per-file fallback chains (`to-ips-sections.label()`,
// `from-patient-assessment`'s `display ?? Definition`, `to-code-search-result.displayOf()`).
//
// Precedence (first non-blank wins, per BRD-1 §6.3 resolution chain):
//   1. `preferredDisplay` — the live dictionary hit. Backend field, additive/optional
//      (Andrew's lane, may not be on the wire yet) — read opportunistically so this
//      formatter lights up the moment the BE lands, with zero FE change.
//   2. `text` — FHIR CodeableConcept.text (the clinician-chosen wording persisted at
//      save; e.g. AllergyIntolerance.code.text).
//   2b. `shortDisplay` — medication rows: the readable name ("Crestor 10 mg") Scrips.Patient
//      #1224 returns next to the DDC capitals `display`. After `text`, so wording a clinician
//      chose at save still wins. Server-cased, so this still never re-cases.
//   3. Raw wire display fields, in the union order of today's chains:
//      `display` → `displayName` → `description` → `shortName` → `Definition`.
//   4. `code` — a code is better than a blank row (rail `label()` precedent).
//
// HARD RULE (Samer's write-time-normalization ruling, BRD-1 §2.3): NO case
// transformation of raw strings here. Casing/keep-list normalization happens at
// record write / dictionary publish, never at render. This function only trims.
//
// Onset year (Samer's locked decision: onset year next to the name, Elation pattern —
// BRD-1 D-2 resolved to onset): derived from `onset` → `onsetDateTime` → `recordedDate`
// where the entry carries one. Returned as data; RENDERING it is the DS
// ClinicalTermRow's job (design lane) — consumers today ignore it (zero visual change).
//
// PURE — no I/O, no locale logic (locale designations resolve server-side per BRD-1 §2.4).

/** Flat Coding-like shape — the union of every wire coding we render
 *  (SummaryCode, ConditionCode, AssessmentCoding, CodeDisplayName, ImmunizationCoding…). */
export interface ClinicalCodingLike {
  /** Live dictionary short display (additive BE field — optional until PROD-1656 BE lands). */
  preferredDisplay?: string | null;
  /** Medication rows: readable short name (Scrips.Patient #1224). Absent on older servers. */
  shortDisplay?: string | null;
  /** FHIR CodeableConcept.text — clinician-chosen wording frozen at save. */
  text?: string | null;
  display?: string | null;
  /** PascalCase wire literal on procedure/lab codings. */
  displayName?: string | null;
  description?: string | null;
  shortName?: string | null;
  /** PascalCase wire literal — ICD-10 master long description. */
  Definition?: string | null;
  code?: string | null;
  system?: string | null;
}

/** Entry-like shape — a condition/allergy/procedure row carrying the date fields the
 *  onset year derives from, with its coding either NESTED (`code` is a Coding object)
 *  or FLAT (the row itself is the coding and `code` is the code string). */
export interface ClinicalEntryLike {
  code?: ClinicalCodingLike | string | null;
  preferredDisplay?: string | null;
  text?: string | null;
  shortDisplay?: string | null;
  /** ISO 8601 (backend emits without trailing Z) or 'n/a' (dataAbsentReason). */
  onset?: string | null;
  onsetDateTime?: string | null;
  recordedDate?: string | null;
}

export interface ClinicalDisplayName {
  /** The name to render. Never case-transformed — trimmed only. '' when nothing usable. */
  name: string;
  /** The raw code (chip content). Undefined when blank/absent. */
  code?: string;
  /** Onset year (Elation "2015 Hypertension" pattern). Undefined when underivable. */
  onsetYear?: number;
}

const blank = (v: unknown): boolean => typeof v !== 'string' || v.trim() === '';
const pick = (...vals: Array<string | null | undefined>): string => {
  for (const v of vals) if (!blank(v)) return (v as string).trim();
  return '';
};

/** Sanity window — rejects garbage like '0001-01-01' (EF default) and typo years. */
const YEAR_MIN = 1900;
const YEAR_MAX = 2100;

/** Extract the year from an ISO 8601 date string ('2015-03-01T…', '2015-03-01',
 *  '2015-03', '2015'). Regex, not `new Date()` — the wire emits ISO without the
 *  trailing Z and carries literal 'n/a' placeholders; Date parsing is locale/UA-
 *  dependent on those. Requires a plausible ISO shape (valid month when present) so
 *  '2015-garbage' / '2015-99-…' never yields a year (Codex pass, minor #2). */
function yearOf(v: string | null | undefined): number | undefined {
  if (typeof v !== 'string') return undefined;
  const m = /^\s*(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?(?:[T\s].*)?\s*$/.exec(v);
  if (!m) return undefined;
  const y = Number(m[1]);
  if (y < YEAR_MIN || y > YEAR_MAX) return undefined;
  if (m[2] !== undefined) {
    const month = Number(m[2]);
    if (month < 1 || month > 12) return undefined;
  }
  if (m[3] !== undefined) {
    const day = Number(m[3]);
    if (day < 1 || day > 31) return undefined;
  }
  return y;
}

/**
 * Resolve the display name for a clinical coding or an entry wrapping one.
 *
 * `clinicalDisplayName({ display: 'Type 2 diabetes', code: 'E11.9' })`
 *   → `{ name: 'Type 2 diabetes', code: 'E11.9' }`
 * `clinicalDisplayName({ code: { preferredDisplay: 'Type 2 diabetes', display: 'Type 2 diabetes mellitus without complications', code: 'E11.9' }, onset: '2015-03-01T00:00:00' })`
 *   → `{ name: 'Type 2 diabetes', code: 'E11.9', onsetYear: 2015 }`
 *
 * Null/undefined/string-only input degrades to `{ name: '' }` / `{ name: code, code }` —
 * callers keep their own per-section fallback labels (`|| 'Problem'` etc.).
 */
export function clinicalDisplayName(
  input: ClinicalCodingLike | ClinicalEntryLike | string | null | undefined,
): ClinicalDisplayName {
  if (input == null) return { name: '' };
  if (typeof input === 'string') {
    const s = input.trim();
    return s ? { name: s, code: s } : { name: '' };
  }

  // An input can be BOTH things at once: a flat coding that also carries entry-level
  // date fields (e.g. a ConditionCode-shaped row with a string `code` plus `onset`).
  // So: unwrap a nested coding when `code` is an object, but read the onset fields
  // from the outer object regardless of nesting (Codex pass, important #2).
  const outer = input as ClinicalEntryLike & ClinicalCodingLike;
  const nested =
    outer.code !== null && typeof outer.code === 'object'
      ? (outer.code as ClinicalCodingLike)
      : undefined;
  const coding: ClinicalCodingLike = nested ?? outer;

  const name = pick(
    coding.preferredDisplay,
    nested ? outer.preferredDisplay : undefined,
    coding.text,
    nested ? outer.text : undefined,
    coding.shortDisplay,
    nested ? outer.shortDisplay : undefined,
    coding.display,
    coding.displayName,
    coding.description,
    coding.shortName,
    coding.Definition,
    typeof coding.code === 'string' ? coding.code : undefined,
  );

  const code =
    typeof coding.code === 'string' && !blank(coding.code) ? coding.code.trim() : undefined;

  const onsetYear =
    yearOf(outer.onset) ?? yearOf(outer.onsetDateTime) ?? yearOf(outer.recordedDate);

  return { name, ...(code ? { code } : {}), ...(onsetYear ? { onsetYear } : {}) };
}
